import {
  Box,
  Button,
  ButtonGroup,
  Card,
  Divider,
  HorizontalGrid,
  HorizontalStack,
  Icon,
  Select,
  Text,
  TextField,
  useBreakpoints,
} from '@shopify/polaris';
import { SearchMinor } from '@shopify/polaris-icons';
import DepositScopeFilter from '../../../UI/Reports/DepositScopeFilter';
import { DEPOSIT_TYPE_OPTIONS, buildDepositScopeLabel } from '../../../../helpers/depositTypes';
import FilterSelect from '../../../UI/Products/FilterSelect';
import DateRangePicker from '../../../UI/Common/DateRangePicker';
import { PERIOD_PRESETS, formatRangeLabel } from './constants';

/**
 * Card de filtros del informe de Reposición. Misma estructura que el
 * de Eficacia pero con `DateRangePicker` + Select de presets en lugar
 * de Temporada/Año.
 */
export const FiltersCard = ({
  presetId,
  setPresetId,
  range,
  setCustomRange,
  depositsValue,
  depositRolesValue,
  depositGroupsValue,
  setDepositScope,
  depositOptions,
  groupOptions,
  filters,
  handleFilter,
  clearFilters,
  familySearch,
  setFamilySearch,
}) => {
  const { lgUp } = useBreakpoints();

  const depositsLabel = buildDepositScopeLabel({
    depositsValue,
    depositRolesValue,
    depositGroupsValue,
    groupOptions,
  });

  const presetOptions = PERIOD_PRESETS.map((p) => ({ label: p.label, value: p.id }));

  const handleDatePickerChange = ({ startDate, endDate }) => {
    if (!startDate || !endDate) return;
    // Convertimos a `toExclusive` (día siguiente al `endDate` inclusivo).
    const toExclusive = new Date(endDate);
    toExclusive.setDate(toExclusive.getDate() + 1);
    setCustomRange({ from: new Date(startDate), toExclusive });
  };

  const isCustom = presetId === 'custom';

  return (
    <Card roundedAbove="sm">
      <HorizontalGrid
        columns={{ xs: '1fr', sm: '1fr', md: '1fr', lg: '3fr 5fr', xl: '3fr 7fr' }}
        gap={{ xs: '3', sm: '3', md: '3', lg: '3', xl: '3' }}
      >
        <Box>
          <Text variant="headingMd" as="h4">
            Búsqueda
          </Text>
          <Box border="divider" borderRadius="base" minHeight=".5rem" />
          <TextField
            prefix={<Icon source={SearchMinor} color="inkLighter" />}
            label="Buscar serie / familia"
            labelHidden
            placeholder="Buscar serie / familia"
            value={familySearch}
            onChange={setFamilySearch}
            clearButton
            onClearButtonClick={() => setFamilySearch('')}
            autoComplete="off"
          />
        </Box>
        <HorizontalStack align={lgUp ? 'end' : 'start'} gap="2" wrap>
          <Box>
            <Text variant="headingMd" as="h4">
              Período
            </Text>
            <Box border="divider" borderRadius="base" minHeight=".5rem" />
            <HorizontalStack gap="2" wrap>
              <Box minWidth="200px">
                <Select label="Período" labelHidden options={presetOptions} value={presetId} onChange={setPresetId} />
              </Box>
              {isCustom && (
                <DateRangePicker
                  onDateChanges={handleDatePickerChange}
                  defaultDates={{
                    startDate: range.from,
                    // El picker espera la fecha inclusiva, no la `toExclusive`.
                    endDate: new Date(range.toExclusive.getTime() - 24 * 60 * 60 * 1000),
                  }}
                />
              )}
              {!isCustom && (
                <Text variant="bodyMd" tone="subdued" as="span">
                  {formatRangeLabel(range.from, range.toExclusive)}
                </Text>
              )}
            </HorizontalStack>
            <Box minHeight=".75rem" />
            <Text variant="headingMd" as="h4">
              Categorización
            </Text>
            <Box border="divider" borderRadius="base" minHeight=".5rem" />
            <ButtonGroup segmented={lgUp}>
              <DepositScopeFilter
                title={depositsLabel}
                allSelected={depositsValue === 'ALL'}
                roles={depositRolesValue ? depositRolesValue.split(':').filter(Boolean) : []}
                groups={depositGroupsValue ? depositGroupsValue.split(':').filter(Boolean) : []}
                deposits={
                  depositsValue && depositsValue !== 'ALL' ? depositsValue.split(':').filter(Boolean) : []
                }
                roleOptions={DEPOSIT_TYPE_OPTIONS}
                groupOptions={groupOptions}
                depositOptions={depositOptions}
                onChange={setDepositScope}
              />
              {Object.values(filters)
                .filter((f) => f.bundle)
                .map((f) => (
                  <FilterSelect
                    {...f}
                    options={f.options ?? []}
                    key={f.slug}
                    defaultValue={f.value ?? ''}
                    onFilterChange={handleFilter}
                  />
                ))}
            </ButtonGroup>
          </Box>
        </HorizontalStack>
      </HorizontalGrid>
      <Box minHeight=".75rem" />
      <Divider />
      <Box minHeight=".5rem" />
      <HorizontalStack align="end">
        <Button onClick={clearFilters} plain>
          Borrar filtros
        </Button>
      </HorizontalStack>
    </Card>
  );
};
